import React, { useState } from 'react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { Edit } from 'lucide-react';
import { toast } from 'sonner';
import { readResponse, readResponseJSON } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCurrency = (val) => {
  const num = parseFloat(val) || 0;
  const prefix = num < 0 ? '-' : '';
  return `${prefix}$${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ManagementBonus = () => {
  const navigate = useNavigate();
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const formatDateInput = (d) => {
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const [projectsPaidFrom, setProjectsPaidFrom] = useState(formatDateInput(thirtyDaysAgo));
  const [projectsPaidTo, setProjectsPaidTo] = useState(formatDateInput(today));
  const [minimumHourlyRate, setMinimumHourlyRate] = useState('1');
  const [minimumMargin, setMinimumMargin] = useState('1');
  const [bonusPercent, setBonusPercent] = useState('1');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleGetBonus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/management-bonus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date_from: projectsPaidFrom,
          date_to: projectsPaidTo,
          min_hourly_rate: parseFloat(minimumHourlyRate) || 0,
          min_margin: parseFloat(minimumMargin) || 0,
          bonus_percent: parseFloat(bonusPercent) || 0,
        }),
      });
      const { data } = await readResponse(res);
      setResults(data);
    } catch (err) {
      console.error('Error fetching bonus data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (item) => {
    if (!window.confirm(`Mark management bonus for ${item.project_id} as paid? It will drop off this report.`)) return;
    try {
      const today = new Date();
      const stamp = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/projects/${item.project_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ mgmt_bonus_paid: true, mgmt_bonus_paid_on: stamp }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Marked ${item.project_id} as paid`);
      handleGetBonus();
    } catch (err) {
      toast.error(`Failed to mark as paid: ${err.message}`);
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-2xl lg:text-3xl text-[#5B8DB8] font-light mb-1" data-testid="bonus-subtitle">Commissions</h2>
          <h1 className="text-3xl lg:text-4xl text-[#5B8DB8] font-light" data-testid="bonus-title">Management Bonus</h1>
        </div>

        <div className="bg-white rounded shadow p-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Panel - Form */}
            <div className="lg:w-80 flex-shrink-0">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <label className="text-red-500 text-sm w-44 text-right whitespace-nowrap">Projects Paid From:</label>
                  <input
                    type="text"
                    value={projectsPaidFrom}
                    onChange={(e) => setProjectsPaidFrom(e.target.value)}
                    placeholder="MM/DD/YYYY"
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-32"
                    data-testid="paid-from-input"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label className="text-red-500 text-sm w-44 text-right whitespace-nowrap">Projects Paid To:</label>
                  <input
                    type="text"
                    value={projectsPaidTo}
                    onChange={(e) => setProjectsPaidTo(e.target.value)}
                    placeholder="MM/DD/YYYY"
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-32"
                    data-testid="paid-to-input"
                  />
                </div>

                <p className="text-xs text-red-500 mt-4 leading-relaxed italic">
                  <strong className="text-green-600 not-italic">Please note:</strong> Projects paid in full but without a paid in full date will automatically show up on this report until the bonus commission is paid to management.
                </p>

                <div className="flex items-center gap-3 mt-4">
                  <label className="text-red-500 text-sm w-44 text-right whitespace-nowrap">Minimum Hourly Rate:</label>
                  <input
                    type="text"
                    value={minimumHourlyRate}
                    onChange={(e) => setMinimumHourlyRate(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-16"
                    data-testid="min-hourly-rate-input"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label className="text-red-500 text-sm w-44 text-right whitespace-nowrap">Minimum Margin:</label>
                  <input
                    type="text"
                    value={minimumMargin}
                    onChange={(e) => setMinimumMargin(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-16"
                    data-testid="min-margin-input"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label className="text-red-500 text-sm w-44 text-right whitespace-nowrap">Bonus Percent:</label>
                  <input
                    type="text"
                    value={bonusPercent}
                    onChange={(e) => setBonusPercent(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-16"
                    data-testid="bonus-percent-input"
                  />
                </div>

                <div className="flex justify-center mt-4">
                  <button
                    onClick={handleGetBonus}
                    disabled={loading}
                    className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50"
                    data-testid="get-bonus-btn"
                  >
                    {loading ? 'Loading...' : 'Get Bonus'}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Panel - Results */}
            <div className="flex-1 min-w-0">
              {results && (
                <>
                  <h2 className="text-3xl lg:text-4xl text-[#5B8DB8] font-light text-center mb-6" data-testid="total-bonus">
                    {fmtCurrency(results.total_bonus)} Bonus
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="bonus-table">
                      <thead>
                        <tr className="border-b-2">
                          <th className="text-left p-2 text-gray-600 font-normal">Project #</th>
                          <th className="text-left p-2 text-gray-600 font-normal">FNOL Date</th>
                          <th className="text-left p-2 text-gray-600 font-normal">Customer</th>
                          <th className="text-left p-2 text-gray-600 font-normal">Job Location</th>
                          <th className="text-left p-2 text-gray-600 font-normal">Job Type</th>
                          <th className="text-right p-2 text-gray-600 font-normal">Value</th>
                          <th className="text-right p-2 text-gray-600 font-normal">Hourly Rate</th>
                          <th className="text-right p-2 text-gray-600 font-normal">Profit Margin</th>
                          <th className="text-right p-2 text-gray-600 font-normal">Bonus</th>
                          <th className="text-left p-2 text-gray-600 font-normal">Date Paid<br/>in Full</th>
                          <th className="text-left p-2 text-gray-600 font-normal">Commission<br/>Paid On</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.projects.map((item, index) => (
                          <tr key={index} className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-testid={`bonus-row-${index}`}>
                            <td className="p-2 text-gray-700">{item.project_id}</td>
                            <td className="p-2 text-gray-700">{item.fnol_date || ''}</td>
                            <td className="p-2 text-gray-700">{item.customer}</td>
                            <td className="p-2">
                              <div className="text-gray-700">{item.job_location}</div>
                              <div className="text-xs text-gray-400">{item.address}</div>
                            </td>
                            <td className="p-0">
                              <div
                                className="px-2 py-2 text-white text-center text-xs h-full flex items-center justify-center"
                                style={{ backgroundColor: item.job_type_color ? `#${item.job_type_color}` : '#ccc' }}
                              >
                                {item.job_type}
                              </div>
                            </td>
                            <td className="p-2 text-gray-700 text-right">{fmtCurrency(item.value)}</td>
                            <td className="p-2 text-right font-medium" style={{ color: '#4ECFDB' }}>
                              {fmtCurrency(item.hourly_rate)}
                            </td>
                            <td className="p-2 text-gray-700 text-right">{item.profit_margin}%</td>
                            <td className="p-2 text-gray-700 text-right">{fmtCurrency(item.bonus)}</td>
                            <td className="p-2 text-gray-700">{item.paid_in_full_date || ''}</td>
                            <td className="p-2 text-gray-700">{item.commission_paid_on || ''}</td>
                            <td className="p-2">
                              <div className="flex items-center gap-1">
                                <button onClick={() => navigate(`/projects/${item.project_id}`)} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1.5 inline-flex" data-testid={`edit-btn-${index}`} title="Open project">
                                  <Edit className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleMarkPaid(item)}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs rounded px-2 py-1.5 whitespace-nowrap"
                                  data-testid={`mark-paid-btn-${index}`}
                                  title="Stamp Commission Paid On date so this row drops off the report"
                                >
                                  Mark as Paid
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {results.projects.length === 0 && (
                          <tr><td colSpan="12" className="text-center py-8 text-gray-500">No projects found matching the criteria</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {!results && !loading && (
                <div className="text-center text-gray-400 py-16" data-testid="no-results-placeholder">
                  Fill in the fields and press "Get Bonus" to calculate management bonuses.
                </div>
              )}
              {loading && (
                <div className="text-center text-gray-500 py-16">Calculating bonuses...</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ManagementBonus;
